// Queue behaviour the guide explains: routing options, hold music/welcome, and
// the callback offer. Everything optional — an untouched field stays undefined.

import type { ReactNode } from 'react';
import type { QueueFlowNode, Prompt } from '../../api/client';
import { inputCls, labelCls, clampInt } from './common';
import { PromptEditor } from './PromptEditor';

export function QueueSettingsForm({ node, onChange, audioPicker }: {
  node: QueueFlowNode;
  onChange: (patch: Partial<QueueFlowNode>) => void;
  audioPicker?: (value: Prompt, onChange: (v: Prompt) => void) => ReactNode;
}) {
  const tri = (label: string, key: 'presenceBasedRouting' | 'conferenceMode' | 'allowOptOut') => (
    <div>
      <label className={labelCls}>{label}</label>
      <select value={node[key] === undefined ? '' : node[key] ? 'yes' : 'no'} className={inputCls}
        onChange={e => onChange({ [key]: e.target.value === '' ? undefined : e.target.value === 'yes' })}>
        <option value="">— not set —</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
  const music = node.music || { useDefault: true };
  const callback = node.callback || { enabled: false };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {tri('Presence-based routing', 'presenceBasedRouting')}
        {tri('Conference mode', 'conferenceMode')}
        {tri('Agents can opt out', 'allowOptOut')}
        <div>
          <label className={labelCls}>Agent alert time (seconds)</label>
          <input type="number" min={15} max={180} value={node.agentAlertTime ?? ''} className={inputCls}
            onChange={e => onChange({ agentAlertTime: clampInt(e.target.value, 180) })} />
        </div>
        <div>
          <label className={labelCls}>Language</label>
          <input type="text" value={node.language || ''} onChange={e => onChange({ language: e.target.value || undefined })} placeholder="nl-NL" className={inputCls} />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-800">Music on hold</span>
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input type="checkbox" checked={music.useDefault} className="accent-purple-600"
              onChange={e => onChange({ music: { ...music, useDefault: e.target.checked } })} />Use the Teams default music
          </label>
        </div>
        {!music.useDefault && (
          <PromptEditor label="Hold music" value={music.hold} onChange={p => onChange({ music: { ...music, hold: p } })} audioPicker={audioPicker} />
        )}
        <PromptEditor label="Welcome greeting" value={music.welcome} onChange={p => onChange({ music: { ...music, welcome: p } })} audioPicker={audioPicker} />
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-800">
          <input type="checkbox" checked={callback.enabled} className="accent-purple-600"
            onChange={e => onChange({ callback: { ...callback, enabled: e.target.checked } })} />Callback offered to waiting callers
        </label>
        {callback.enabled && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Key to request callback</label>
              <input type="text" maxLength={1} value={callback.dtmf || ''} placeholder="1" className={inputCls}
                onChange={e => onChange({ callback: { ...callback, dtmf: e.target.value.replace(/[^0-9*#]/g, '').slice(0, 1) || undefined } })} />
            </div>
            <div>
              <label className={labelCls}>Offer after waiting (seconds)</label>
              <input type="number" min={0} max={3600} value={callback.waitSeconds ?? ''} className={inputCls}
                onChange={e => onChange({ callback: { ...callback, waitSeconds: clampInt(e.target.value, 3600) } })} />
            </div>
            <div>
              <label className={labelCls}>Or when calls waiting reach</label>
              <input type="number" min={0} max={200} value={callback.callsInQueue ?? ''} className={inputCls}
                onChange={e => onChange({ callback: { ...callback, callsInQueue: clampInt(e.target.value, 200) } })} />
            </div>
            <div>
              <label className={labelCls}>Service level target (seconds)</label>
              <input type="number" min={0} max={3600} value={callback.serviceLevelSeconds ?? ''} className={inputCls}
                onChange={e => onChange({ callback: { ...callback, serviceLevelSeconds: clampInt(e.target.value, 3600) } })} />
            </div>
            <div className="col-span-2">
              <PromptEditor label="Callback offer message" value={callback.prompt} onChange={p => onChange({ callback: { ...callback, prompt: p } })} audioPicker={audioPicker} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
